/**
 * "My Human Reviews" page — the user's review requests with status, cancel for
 * pending, and the full result for completed items.
 *
 * @module HumanReviewsPage
 */
import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { StandardPageShell } from "@/components/layout/StandardPageShell";
import { ReviewStatusBadge } from "@/components/humanReview/ReviewStatusBadge";
import { ReviewResultPanel } from "@/components/humanReview/ReviewResultPanel";
import {
  useCancelReviewRequest,
  useReviewRequestDetail,
  useReviewRequests,
} from "@/hooks/useHumanReviews";
import { useMarkNotificationsRead } from "@/hooks/useNotifications";
import { NOTIFICATION_HUMAN_REVIEW_COMPLETED } from "@/lib/notificationsApi";
import type { ReviewListItem } from "@/lib/humanReviewsApi";

function DetailLabel({ children }: { readonly children: React.ReactNode }) {
  return (
    <p className="font-ui text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
      {children}
    </p>
  );
}

function CompletedResult({ requestId }: { readonly requestId: string }) {
  const { t } = useTranslation();
  const { detail, isLoading } = useReviewRequestDetail(requestId);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-3 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
      </div>
    );
  }
  if (!detail?.result) return null;

  const hasAiFeedback =
    detail.aiNote ||
    detail.aiStrength ||
    detail.aiWeakness ||
    detail.aiGrammarFeedback ||
    detail.aiStructureFeedback ||
    detail.aiPronunciationFeedback;

  return (
    <div className="space-y-4 pt-1">
      {/* Question context (passage or inline material) */}
      {(detail.contextText || detail.questionMaterialText) && (
        <div className="space-y-1">
          <DetailLabel>{t("humanReview.detail.questionContext")}</DetailLabel>
          <p className="font-ui rounded-md border border-border bg-muted/30 p-3 text-sm leading-relaxed text-foreground whitespace-pre-wrap">
            {detail.contextText || detail.questionMaterialText}
          </p>
        </div>
      )}

      {/* Exam question */}
      {detail.questionText && (
        <div className="space-y-1">
          <DetailLabel>{t("humanReview.detail.examQuestion")}</DetailLabel>
          <p className="font-ui text-sm leading-relaxed text-foreground whitespace-pre-wrap">
            {detail.questionText}
          </p>
        </div>
      )}

      {/* User's answer */}
      {(detail.answerText || detail.audioUrl) && (
        <div className="space-y-1">
          <DetailLabel>{t("humanReview.detail.yourAnswer")}</DetailLabel>
          {detail.answerText ? (
            <p className="font-ui rounded-md border border-border bg-muted/30 p-3 text-sm leading-relaxed text-foreground whitespace-pre-wrap">
              {detail.answerText}
            </p>
          ) : (
            <p className="font-ui text-sm italic text-muted-foreground">
              {t("humanReview.detail.spokenAnswer")}
            </p>
          )}
        </div>
      )}

      {/* AI feedback */}
      {hasAiFeedback && (
        <div className="space-y-2 rounded-lg border border-border bg-muted/20 p-3">
          <DetailLabel>{t("humanReview.detail.aiFeedback")}</DetailLabel>
          {detail.aiNote && (
            <p className="font-ui text-sm leading-relaxed text-foreground whitespace-pre-wrap">
              {detail.aiNote}
            </p>
          )}
          {detail.aiStrength && (
            <div className="space-y-0.5">
              <p className="font-ui text-[11px] font-medium text-muted-foreground">
                {t("humanReview.detail.strength")}
              </p>
              <p className="font-ui text-sm text-foreground whitespace-pre-wrap">
                {detail.aiStrength}
              </p>
            </div>
          )}
          {detail.aiWeakness && (
            <div className="space-y-0.5">
              <p className="font-ui text-[11px] font-medium text-muted-foreground">
                {t("humanReview.detail.weakness")}
              </p>
              <p className="font-ui text-sm text-foreground whitespace-pre-wrap">
                {detail.aiWeakness}
              </p>
            </div>
          )}
          {detail.aiGrammarFeedback && (
            <div className="space-y-0.5">
              <p className="font-ui text-[11px] font-medium text-muted-foreground">
                {t("humanReview.detail.grammarFeedback")}
              </p>
              <p className="font-ui text-sm text-foreground whitespace-pre-wrap">
                {detail.aiGrammarFeedback}
              </p>
            </div>
          )}
          {detail.aiStructureFeedback && (
            <div className="space-y-0.5">
              <p className="font-ui text-[11px] font-medium text-muted-foreground">
                {t("humanReview.detail.structureFeedback")}
              </p>
              <p className="font-ui text-sm text-foreground whitespace-pre-wrap">
                {detail.aiStructureFeedback}
              </p>
            </div>
          )}
          {detail.aiPronunciationFeedback && (
            <div className="space-y-0.5">
              <p className="font-ui text-[11px] font-medium text-muted-foreground">
                {t("humanReview.detail.pronunciationFeedback")}
              </p>
              <p className="font-ui text-sm text-foreground whitespace-pre-wrap">
                {detail.aiPronunciationFeedback}
              </p>
            </div>
          )}
        </div>
      )}

      {/* User's optional notes to the reviewer (attachedQuestion already visible in the card) */}
      {detail.notes && (
        <div className="space-y-1">
          <DetailLabel>{t("humanReview.detail.yourNotes")}</DetailLabel>
          <p className="font-ui text-sm leading-relaxed text-foreground whitespace-pre-wrap">
            {detail.notes}
          </p>
        </div>
      )}

      <div className="border-t border-border pt-3">
        <ReviewResultPanel result={detail.result} />
      </div>
    </div>
  );
}

function ReviewRow({ item }: { readonly item: ReviewListItem }) {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const cancelReview = useCancelReviewRequest();

  const moduleLabel = t(`moduleSelection.modules.${item.module}.label`, {
    defaultValue: item.module,
  });
  const submitted = new Date(item.createdAt).toLocaleDateString(i18n.language);

  const handleCancel = async () => {
    const result = await cancelReview.mutateAsync(item.id);
    if (result) {
      toast.success(t("humanReview.list.cancelled"));
    } else {
      toast.error(t("humanReview.list.cancelError"));
    }
  };

  return (
    <Card className="border-border">
      <CardContent className="space-y-3 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <ReviewStatusBadge status={item.status} />
            <span className="font-ui text-sm font-semibold text-foreground">
              {moduleLabel}
            </span>
            <span className="font-ui text-xs text-muted-foreground">
              {t("humanReview.list.submitted")}: {submitted}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {item.status === "completed" && item.hasResult && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setExpanded((v) => !v)}
              >
                {t("humanReview.list.viewFeedback")}
              </Button>
            )}
            {item.status === "pending" && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={cancelReview.isPending}
                  >
                    {cancelReview.isPending && (
                      <Loader2
                        className="mr-2 h-3.5 w-3.5 animate-spin"
                        aria-hidden
                      />
                    )}
                    {t("humanReview.list.cancel")}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      {t("humanReview.list.cancelConfirmTitle")}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      {t("humanReview.list.cancelConfirmDescription")}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>
                      {t("humanReview.list.cancelConfirmKeep")}
                    </AlertDialogCancel>
                    <AlertDialogAction onClick={handleCancel}>
                      {t("humanReview.list.cancelConfirmAction")}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>

        <p className="font-ui text-sm text-foreground/80">
          {item.attachedQuestion}
        </p>

        {expanded && item.status === "completed" && (
          <CompletedResult requestId={item.id} />
        )}
      </CardContent>
    </Card>
  );
}

export default function HumanReviewsPage() {
  const { t } = useTranslation();
  const { requests, isLoading } = useReviewRequests();
  const markRead = useMarkNotificationsRead();

  // Opening the page clears the "new result" unread indicator.
  useEffect(() => {
    void markRead.mutateAsync(NOTIFICATION_HUMAN_REVIEW_COMPLETED);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StandardPageShell
      title={t("humanReview.page.title")}
      headerRight={<LanguageSwitcher />}
      className="mx-auto w-full max-w-7xl flex-1 px-3 py-4 sm:px-6 sm:py-8 lg:px-8"
    >
      <p className="font-ui mb-6 text-sm text-muted-foreground">
        {t("humanReview.page.subtitle")}
      </p>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center gap-3 py-16 text-muted-foreground">
          <Loader2
            className="h-8 w-8 animate-spin text-primary/40"
            aria-hidden
          />
        </div>
      ) : requests.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center">
            <p className="font-ui text-sm text-muted-foreground">
              {t("humanReview.page.empty")}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {requests.map((item) => (
            <ReviewRow key={item.id} item={item} />
          ))}
        </div>
      )}
    </StandardPageShell>
  );
}
